<template>
  <div>
    <div v-show="imageOpened" class="draggable-image__wrapper">
      <img :src="imgSrc" class="draggable-image__img" alt="Content Image" />
    </div>
    <div
      ref="draggableContainer"
      :class="['draggable-image', imageOpened && 'draggable-image--draggable']"
      @mousedown="dragMouseDown"
      @mouseenter="hoverHandler(true)"
      @mouseleave="hoverHandler(false)"
      @click="openImage()"
    >
      <div
        :class="[
          'draggable-image__wrapper',
          imageOpened && 'draggable-image__wrapper--draggable',
        ]"
      >
        <XIcon
          v-if="showIcons"
          class="draggable-image__close"
          @click.stop="closeImage()"
        />
        <img
          ref="image"
          :src="imgSrc"
          class="draggable-image__img"
          alt="Content Image"
        />
        <img
          v-show="hasResize && showIcons"
          ref="resizeIcon"
          src="@/assets/images/labs/resize-image-icon.png"
          class="draggable-image__resize-icon"
          @mousedown="resizeImage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { XIcon } from 'vue-feather-icons'

export default {
  name: 'DraggableImage',
  components: {
    XIcon,
  },
  props: {
    imgSrc: {
      type: String,
      required: true,
    },
    hasResize: {
      type: Boolean,
      required: false,
      default: true,
    },
    offsetTop: {
      type: Number,
      required: false,
      default: 0,
    },
  },
  data() {
    return {
      imageOpened: false,
      showIcons: false,
      draggableContainer: null,
      minimumElementWidth: 450,
      positions: {
        clientX: undefined,
        clientY: undefined,
        movementX: 0,
        movementY: 0,
        resizeStartX: 0,
        resizeStartY: 0,
        resizeStartWidth: 0,
        resizeStartHeight: 0,
      },
    }
  },
  mounted() {
    this.draggableContainer = this.$refs.draggableContainer
  },
  methods: {
    openImage() {
      if (this.imageOpened) return
      const draggableContainer = this.$refs.draggableContainer
      const offsetRight =
        window.innerWidth -
        draggableContainer.offsetLeft -
        draggableContainer.offsetWidth
      draggableContainer.style.top =
        draggableContainer.offsetParent.offsetTop + 'px'
      draggableContainer.style.right = offsetRight + 20 + 'px'
      draggableContainer.style.left = 'unset'
      this.imageOpened = true
    },
    closeImage() {
      this.$refs.draggableContainer.removeAttribute('style')
      this.showIcons = false
      this.imageOpened = false
    },
    dragMouseDown: function(event) {
      const isResizeIconClicked = this.$refs.resizeIcon?.contains(event.target)
      if (!this.imageOpened || isResizeIconClicked) return
      event.preventDefault()
      this.positions.clientX = event.clientX
      this.positions.clientY = event.clientY
      document.onmousemove = this.elementDrag
      document.onmouseup = this.cleanEventMouseHandlers
    },
    elementDrag: function(event) {
      event.preventDefault()
      const draggableContainer = this.draggableContainer
      const { clientX, clientY } = event
      const movementX = this.positions.clientX - clientX
      const movementY = this.positions.clientY - clientY
      this.positions = {
        ...this.positions,
        clientX,
        clientY,
        movementX,
        movementY,
      }
      const newX = draggableContainer.offsetLeft - this.positions.movementX
      const newY = draggableContainer.offsetTop - this.positions.movementY
      draggableContainer.style.top =
        this.normalizeRange(
          newY,
          window.innerHeight - draggableContainer.clientHeight - this.offsetTop
        ) + 'px'
      draggableContainer.style.left =
        this.normalizeRange(
          newX,
          window.innerWidth - draggableContainer.clientWidth
        ) + 'px'
    },
    resizeImage: function(event) {
      event.preventDefault()
      this.positions.resizeStartX = event.clientX
      this.positions.resizeStartY = event.clientY
      this.positions.resizeStartWidth = parseInt(
        document.defaultView.getComputedStyle(this.draggableContainer).width,
        10
      )
      this.positions.resizeStartHeight = parseInt(
        document.defaultView.getComputedStyle(this.draggableContainer).height,
        10
      )
      document.onmousemove = this.doResizeDrag
      document.onmouseup = this.cleanEventMouseHandlers
    },

    doResizeDrag(event) {
      const { clientWidth } = this.draggableContainer
      const { movementX, offsetX } = event
      const imageOverflow = offsetX >= window.innerWidth - 20
      if (
        (clientWidth < this.minimumElementWidth && movementX < 1) ||
        imageOverflow
      )
        return
      const newResizedEventWidth =
        this.positions.resizeStartWidth +
        event.clientX -
        this.positions.resizeStartX
      const minResizedWidth = Math.max(
        newResizedEventWidth,
        this.minimumElementWidth
      )
      this.draggableContainer.style.width = minResizedWidth + 'px'
      this.draggableContainer.style.height =
        this.positions.resizeStartHeight +
        this.draggableContainer.style.width / this.positions.resizeStartY +
        'px'
    },
    cleanEventMouseHandlers() {
      document.onmouseup = null
      document.onmousemove = null
    },
    hoverHandler(value) {
      if (this.imageOpened) this.showIcons = value
    },
    normalizeRange(value, max) {
      if (value > 0 && value < max) return value
      return value < 1 ? 1 : max - 1
    },
  },
}
</script>

<style lang="scss" scoped>
.draggable-image {
  z-index: 9;
  &--draggable {
    position: absolute;
    width: min(60vw, 600px);
    z-index: 1000;
  }
  &__wrapper {
    position: relative;
    display: flex;
    z-index: 10;
    cursor: zoom-in;
    &:hover:not(.draggable-image__wrapper--draggable) {
      filter: brightness(60%);
    }
    .draggable-image__resize-icon {
      display: none;
    }
    &--draggable {
      width: 100%;
      height: 100%;
      position: static;
      cursor: move;
      .draggable-image__img {
        border-radius: 10px;
      }
      .draggable-image__resize-icon {
        display: block;
        position: absolute;
        right: 0;
        bottom: 0;
        cursor: nwse-resize;
        user-select: none;
      }
    }
    .draggable-image__img {
      width: 100%;
    }
  }
  &__close {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 150;
    cursor: pointer;
    background: var(--black-25);
    border-radius: 5px;
  }
}
</style>
